import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Iter } from 'src/main/webapp/assets/models/iter.model';
import { TenantService } from './tenant/tenant.service';
import { OfflineCacheService } from './services/cache/offline-cache.service';
import * as tenant from '../../../../tenant.json';

/**ToDo Documentazione */
@Injectable({
  providedIn: 'root'
})
export class RestTmtService {

  /** Cache del profilo tenant risolto: la risoluzione avviene una sola volta per sessione. */
  private cachedClient: any = null;

  constructor(
    private readonly logger: NGXLogger,
    private tenantService: TenantService,
    private offlineCache: OfflineCacheService
  ) { }

  getClient() {
    if (this.cachedClient) {
      return this.cachedClient;
    }

    const tenantData = JSON.parse(JSON.stringify(tenant)).default;
    const hostname = location.hostname;

    // Strategia 1: ricerca per corrispondenza URL esatta (esclude 'localhost').
    // Permette nomi di profilo indipendenti dagli hostname dei clienti.
    if (hostname !== 'localhost') {
      const matchedKey = Object.keys(tenantData).find(key => {
        const urls: string[] = tenantData[key].urls ?? [];
        return urls
          .filter((url: string) => url !== 'localhost')
          .some((url: string) => url === hostname);
      });
      if (matchedKey) {
        this.logger.info(`Tenant risolto per URL "${hostname}" → profilo "${matchedKey}"`);
        this.cachedClient = tenantData[matchedKey];
        return this.cachedClient;
      }
    }

    // Strategia 2 (fallback): vecchio comportamento — chiave = primo segmento hostname.
    // Usato in sviluppo locale (localhost) e come safety net.
    const tenantKey = this.tenantService.getTenant();
    this.cachedClient = tenantData[tenantKey] ?? null;
    return this.cachedClient;
  }

  loadStyleTenant(): Promise<void> {
    return this.tenantService.loadTenantStyles(this.getClient());
  }

  public getIter(bookID: string, lang: string): Observable<Iter> {
    let client = this.getClient();
    this.logger.info(`Sto recuperando l'itinerario: ${bookID}`);
    const data = {
      parameters: {
        bookingId: bookID
      },
      xml: 'BasketSearchRQ',
      language: lang
    };

    const url = `${client.tmtEndPoint}/GetItineraryByDays`;

    // Usa cache first per gli itinerari (dati che cambiano raramente)
    return this.offlineCache.postWithCache<Iter>(url, data, {
      networkFirst: true,
      expiryMinutes: 30 // Cache per 30 minuti
    });
  }

  public getIterFake(): Observable<Iter> {
    this.logger.info('Sto recuperando l\'itinerario di test');
    const url = '../assets/json-fake.json';

    // Usa cache first per i dati fake (statici)
    return this.offlineCache.cacheFirst<Iter>(url, {
      expiryMinutes: 60 // Cache per 1 ora
    });
  }

  getFooter(consolidator: any): Observable<string> {
    const url = `../assets/custom/${consolidator}/html/footer.html`;

    // Usa cache first per i footer (contenuto statico)
    return this.offlineCache.cacheFirst<string>(url, {
      expiryMinutes: 120 // Cache per 2 ore
    });
  }

  postFeedback(email: any, itinerary: any, price: any, message: any, bookID: any): Observable<Iter> {
    let client = this.getClient();
    this.logger.info(`Sto rilasciando un feedback per l'itinerario: ${bookID}`);
    const data = {
      bookId: bookID,
      emailFrom: email,
      message: message,
      votes: { itinerary: itinerary, price: price },
      language: this.getClient().defaultTmtLanguage
    };

    const url = `${client.tmtEndPoint}/ItineraryFeedback`;

    // Network first per i feedback (dati che devono essere inviati)
    return this.offlineCache.postWithCache<Iter>(url, data, {
      networkFirst: true,
      expiryMinutes: 10 // Cache breve per retry offline
    });
  }

  getCustomerOutputAdvInformations(pin: any, bookID: any): Observable<any> {
    let client = this.getClient();
    this.logger.info(`Può impostare l'ADV information: ${bookID}`);
    const data = {
      bookingId: bookID,
      pin: pin
    };

    const url = `${client.tmtEndPoint}/GetCustomerOutputAdvInformations`;

    // Cache first per le informazioni ADV (cambiano raramente)
    return this.offlineCache.postWithCache<any>(url, data, {
      networkFirst: true,
      expiryMinutes: 60 // Cache per 1 ora
    });
  }

  getCustomerOutputAdvInformationsHistory(pin: any, bookID: any): Promise<any> {
    return new Promise((resolve, reject) => {
      let client = this.getClient();
      this.logger.info(`get storico l'ADV information: ${bookID}`);
      const data = {
        bookingId: bookID,
        pin: pin
      };

      const url = `${client.tmtEndPoint}/GetCustomerOutputAdvInformationsHistory`;

      // Cache first per lo storico (dati che cambiano raramente)
      this.offlineCache.postWithCache<any>(url, data, {
        networkFirst: true,
        expiryMinutes: 120 // Cache per 2 ore
      }).subscribe(
        response => resolve(response),
        error => reject(error)
      );
    });
  }

  setCustomerOutputAdvInformations(pin: any, bookID: any, feeFlight: any, feeFlightTaxes: any, feeLandServices: any, footerText: any, hidePriceB2C: any): Observable<any> {
    let client = this.getClient();
    this.logger.info(`Sto settando l'adv information per: ${bookID}`);
    const data = {
      bookingId: bookID,
      pin: pin,
      feeFlight: feeFlight,
      feeFlightTaxes: feeFlightTaxes,
      feeLandServices: feeLandServices,
      footerText: footerText,
      hidePriceB2C: hidePriceB2C
    };

    const url = `${client.tmtEndPoint}/SetCustomerOutputAdvInformations`;

    // Network first per il set (dati che devono essere salvati)
    return this.offlineCache.postWithCache<any>(url, data, {
      networkFirst: true,
      expiryMinutes: 5 // Cache molto breve per retry
    }).pipe(
      tap(async (response) => {
        if (response && (response.success || response.action_result === 'success' || response.action_result === 'ok')) {
          // Invalida la cache dei getter relativi
          const getUrl = `${client.tmtEndPoint}/GetCustomerOutputAdvInformations`;
          const historyUrl = `${client.tmtEndPoint}/GetCustomerOutputAdvInformationsHistory`;
          const commonBody = { bookingId: bookID, pin: pin };

          await this.offlineCache.invalidate(getUrl, commonBody);
          await this.offlineCache.invalidate(historyUrl, commonBody);
          this.logger.info(`Cache invalidata per ${bookID} dopo aggiornamento fee`);
        }
      })
    );
  }

  /**
   * Gestisce le immagini dinamiche con cache offline
   */
  async getDynamicImage(imagePath: string): Promise<string> {
    let client = this.getClient();
    const imageUrl = `${client.photoEndPoint}${imagePath}`;

    try {
      // Prima prova a recuperare dalla cache
      const cachedImage = await this.offlineCache.getCachedImage(imageUrl);
      if (cachedImage) {
        this.logger.info(`Immagine recuperata dalla cache: ${imagePath}`);
        return cachedImage;
      }

      // Se non è in cache, scarica e cacheizza
      this.logger.info(`Scaricando e cacheizzando immagine: ${imagePath}`);
      return await this.offlineCache.cacheImage(imageUrl);
    } catch (error) {
      this.logger.error(`Errore nel recupero dell'immagine ${imagePath}:`, error);
      // Ritorna un'immagine placeholder o l'URL originale
      return imageUrl;
    }
  }

  /**
   * Pre-carica le immagini di un itinerario per uso offline
   */
  async preloadItineraryImages(itinerary: Iter): Promise<void> {
    if (!itinerary || !itinerary.days) {
      return;
    }

    const imagePromises: Promise<string>[] = [];

    // Estrae tutte le immagini dall'itinerario
    itinerary.days.forEach((day: any) => {
      if (day.services) {
        day.services.forEach((service: any) => {
          if (service.photos && service.photos.length > 0) {
            service.photos.forEach((photo: any) => {
              if (photo.url) {
                imagePromises.push(this.getDynamicImage(photo.url));
              }
            });
          }
        });
      }
    });

    try {
      await Promise.all(imagePromises);
      this.logger.info(`Pre-caricamento completato per ${imagePromises.length} immagini`);
    } catch (error) {
      this.logger.warn('Alcuni errori durante il pre-caricamento delle immagini:', error);
    }
  }

  /**
   * Pulisce la cache scaduta
   */
  async cleanExpiredCache(): Promise<void> {
    await this.offlineCache.cleanExpiredCache();
  }

  /**
   * Pulisce tutta la cache offline
   */
  async clearOfflineCache(): Promise<void> {
    await this.offlineCache.clearAllCache();
  }
}
